
import React, { useRef, useEffect, useState } from 'react';
import config from '../../assets/config.json'
import MapView from "@arcgis/core/views/MapView";
import WebMap from "@arcgis/core/WebMap";
import BasemapGallery from "@arcgis/core/widgets/BasemapGallery";
import Expand from '@arcgis/core/widgets/Expand';
import styles from './Map.module.scss';
import ScaleBar from "@arcgis/core/widgets/ScaleBar";
import MenuPanel from '../MenuPanel/MenuPanel';
import { CalciteShell, CalciteSwitch, CalciteLoader, CalciteAlert } from "@esri/calcite-components-react";
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import LastUpdated from '../LastUpdated/LastUpdated';
import { buildBrownfieldsPopup, buildSitesPopup } from '../../util/PopupHelper';
import { handleExtensionActions } from '../../util/ExtensionHelper';
import LocatorSearchSource from '@arcgis/core/widgets/Search/LocatorSearchSource';
import Legend from "@arcgis/core/widgets/Legend";

const Map = (props) => {
  const [showUI, setShowUI] = useState<boolean>(false);
  const [extensionActionFailed, setExtensionActionFailed] = useState<boolean>(false);
  const [canMakePanel, setCanMakePanel] = useState<boolean>(false);
  const [sourcesForSearching, setSourcesForSearching] = useState<Array<any>>([]);
  const [errorMessage, setErrorMessage] = useState<string>('The URL you entered is not a valid search. Please check the URL and try again.');
  const mapDiv = useRef(null);
  const bmGallery = useRef(null);
  const scaleDiv = useRef(null);
  const legendDiv = useRef(null);

  const [extentForMap] = useState({
    xmin: -14551929.45,
    xmax: -7497708.98,
    ymin: 2253932.55,
    ymax: 7067630.84,
    spatialReference: new SpatialReference({ 'wkid': 102100 })
  });

  const [webmap] = useState<WebMap>(new WebMap({
    portalItem: {
      // id: "d5133b7a038c4374a343f95828761840"
      id: process.env.REACT_APP_WEBMAP_ID
    },
  }));

  const [view] = useState<MapView>(new MapView({
    map: webmap,
    extent: extentForMap,
    constraints: { minZoom: 3 }
  }));

  const [basemapGallery] = useState<BasemapGallery>(new BasemapGallery({
    view: view
  }));

  const [galleryExpand] = useState<Expand>(new Expand({
    view: view,
    content: basemapGallery,
    expandTooltip: 'Change Basemap'
  }));

  const [scaleBar] = useState<ScaleBar>(new ScaleBar({
    view: view,
    unit: 'non-metric'
  }));
  
  const [legend] = useState<Legend>(new Legend({
    view: view,
    // layerInfos: [{
    //   layer: webmap.layers.find(layer => layer.title === "Sites")
    // }]
  }));

  const [legendExpand] = useState<Expand>(new Expand({
    view: view,
    content: legend,
    expanded: true,
    expandTooltip: 'Sites Legend'
  }));

  // add watershedLayer to map for search queries
  const [watershedLayer] = useState<FeatureLayer>(new FeatureLayer({
    url: 'https://hydro.nationalmap.gov/arcgis/rest/services/wbd/MapServer/4',
    visible: false,
    title: "Watersheds (HUC 8)",
    listMode: 'hide'
  }));
  webmap.layers.add(watershedLayer);

  // this layer will hold the graphic objects for latest site circles
  const [latestSitesLayer] = useState<GraphicsLayer>(new GraphicsLayer({
  title: "Latest Sites",
  visible: true,
    listMode: "hide",
  }));
  webmap.layers.push(latestSitesLayer);

  // query for the latest sites and draw their circles
  const populateLatestSites = (webmap) => {
    // const sitesLayer = webmap.layers.find(e => e.title === "Sites");
    const sitesLayer = webmap.layers.items.find(
      (e) => e.allLayers && e.allLayers.items.find(
        (p: any) => p.title === config.layerNames.sitesLayer.title
        )
    )
    .allLayers.items.find(
      (p: any) => p.title === config.layerNames.sitesLayer.title
      )
    ;
    let query = sitesLayer.createQuery();
    query.where = "NEW_SITE = 'Y'";
    query.outFields = ["OBJECTID"];
    query.returnGeometry = true;
    query.returnExceededLimitFeatures = true;
    let queryPromise = sitesLayer.queryFeatures(query)
      .catch((e) => { console.error("error occured during query!", e) });
    Promise.resolve(queryPromise).then((allResults) => {
      allResults["features"].forEach((result) => {
        const point = new Point({
          longitude: result.geometry.longitude,
          latitude: result.geometry.latitude
        });
        const symbol = {
          type: "simple-marker",
          color: [173, 173, 173, 0.35],
          outline: {
            color: [0, 0, 0],
            width: 1
          }
        };
        const pointGraphic = new Graphic({
          geometry: point,
          symbol: symbol
        });
        latestSitesLayer.add(pointGraphic);
      });
    });
  }

  // toggle the visibility of the latestSitesLayer
  const toggleLatestSites = () => {
    latestSitesLayer.visible = !latestSitesLayer.visible;
  }

  // toggle which sea level rise layer should be visible
  const toggleSeaRiseLayers = (sliderValue, newSliderValue) => {
    // if the sea rise slider value is 0, set the group layer to not visible
    if (newSliderValue === 0) {
      webmap.allLayers.forEach((layer) => {
        if (layer.title === "Sea Level Rise Scenarios (ft)") {
          layer.visible = false;
        }
      });
    } else {
      // if the sea rise slider value is not 0, find the layer to hide and the layer to show
      let oldLayerTitle = "SLR " + sliderValue + "ft";
      let newLayerTitle = "SLR " + newSliderValue + "ft";
      webmap.allLayers.forEach((layer) => {
        if (layer.title === oldLayerTitle) {
          layer.visible = false;
        } else if (layer.title === newLayerTitle || layer.title === "Sea Level Rise Scenarios (ft)") {
          layer.visible = true;
        }
      });
    }
    // update the active sea rise value
  }

  const makeSourceListForSearch = () => {
    const sourceList: Array<any> = [new LocatorSearchSource({
      name: "Address/Coordinate",
      placeholder: "Search by address or coordinate",
      apiKey:
        "AAPTxy8BH1VEsoebNVZXo8HurEnmVWvNc2sqDcc0JvnHUsGU56u_pX3Up8AHmjBfsaSVfkgOujNmyZsgNsa2YQSw72cEXvnRKpUnq2OHT17E9cnZeKGnfcz_2Ew6slnPo0ZNQIQGwSpowXW0qDOBZISg3Fv6lJLXIxH7T2cotiPQ4C8meU1X2lkXNDgEXRR2YgJ8O7eh1Pq9zz7QzECGJPLMsf4B5osSJKdYdOkc5Hrs5L0.AT1_b8lZUXdN",
      singleLineFieldName: "SingleLine",
      url: "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer",
      countryCode: "US",
    }),
       ...config.sourceList
      ];
    // set the layers that query from tables
    webmap.tables.forEach((table) => {
      if (table.title === "CIMC ALT NAME") {
        sourceList[10].layer = table;
        sourceList[10].popupEnabled = false;
      } else if (table.title === "CIMC ACRES JURISDICTION GRANT") {
        sourceList[13].layer = table;
      }
    });
    // set the layers that query from layers
    webmap.allLayers.forEach((layer) => {
      if (layer.title === "Zip Codes") {
        sourceList[1].layer = layer;
      } else if (layer.title === "State Outlines") {
        sourceList[2].layer = layer;
      } else if (layer.title === "Counties") {
        sourceList[3].layer = layer;
      } else if (layer.title === "EPA Regions") {
        sourceList[4].layer = layer;
      } else if (layer.title === "Congressional Districts 117th") {
        sourceList[5].layer = layer;
      } else if (layer.title === "Watersheds (HUC 8)") {
        sourceList[9].layer = layer;
      } else if (layer.title === "Sites") {
        sourceList[11].layer = layer;
      }
      // now to handle layers that are group layers
      // else if (layer.title === "DOE Legacy Management Sites") {
      else if (layer.title === "Legacy Management Sites") {
        sourceList[12].layer = layer;
      } else if (layer.title === "Tribal - Alaska Native Allotments") {
        sourceList[6].layer = layer;
      } else if (layer.title === "Tribal - Alaska Native Villages") {
        sourceList[7].layer = layer;
      } else if (layer.title === "Tribal - American Indian Reservations") {
        sourceList[8].layer = layer;
      }
    });
    setSourcesForSearching(sourceList);
  }


  // this function is used for debugging
  // returns all layer titles and their indeces
  const getLayerIndeces = (webmap) => {
    let layerList = webmap.layers.items;
    // let layerList = webmap.allLayers.items;
    let index = 0;
    layerList.forEach(element => {
      console.log("index: " + index + ", title: " + element.title);
      index++;
    });
  }

  useEffect(() => {
    if (mapDiv.current && !view.container &&
        bmGallery.current && !galleryExpand.container &&
        scaleDiv.current && !scaleBar.container &&
        legendDiv.current && !legendExpand.container) {
      view.container = mapDiv.current;
      galleryExpand.container = bmGallery.current;
      scaleBar.container = scaleDiv.current;
      legendExpand.container = legendDiv.current;
      // view.ui.add('getExtentDiv', 'top-right');
    }
  }, [galleryExpand, scaleBar, view, legendExpand]);

  useEffect(() => {
    view.when(() => {
      view.ui.add('togglerDiv', 'top-right');
      view.ui.add(galleryExpand, { position: 'top-right' });
      view.ui.add(scaleBar, { position: 'bottom-right' });
      view.ui.add('lastUpdatedDiv', 'bottom-right');
      view.ui.add(legendExpand, 'top-right');
      view.ui.move('zoom', 'top-right');
      setShowUI(true);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
useEffect(() => {
  webmap.when(async () => {
    // console.log('webmap:', webmap);
    populateLatestSites(webmap);
    buildBrownfieldsPopup(webmap);
    buildSitesPopup(webmap);
    
    // set legend layerInfos once layer can be found from webmap
    // Filter visible layers and convert to an array
    const visibleLayers = webmap.layers.toArray().filter(layer => layer.visible);

    // Map visible layers to legend layerInfos with custom title and convert to a regular array
    const layerInfos = visibleLayers.map(layer => ({
      layer: layer,
      title: `Legend`
    }));

    // Set the legend's layerInfos property with a regular array
    legend.layerInfos = layerInfos;
    
    // retrieveLastUpdated(webmap);
    webmap.reorder(latestSitesLayer, webmap.layers.length - 1);
    makeSourceListForSearch();
    // webmap.reorder(watershedLayer, webmap.layers.length - 2);
    setCanMakePanel(true);
    if (props.extensionData) {
      // console.log('before extension: ', props.extensionData);
      const extensionAction = await handleExtensionActions(webmap, view, props.extensionData);
      // console.log('after extension: ', extensionAction);
      if (extensionAction !== 'success') {
        setExtensionActionFailed(true);
        setErrorMessage(extensionAction);
      }
    }
    // use this function below to get the indeces and titles of layers on the webmap
    let debug = 0;
    if (debug) {
      getLayerIndeces(webmap);
    }
  });
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

useEffect(() => {}, [sourcesForSearching, canMakePanel, showUI, extensionActionFailed, view, webmap]);
  return (
    <>
      <CalciteShell content-behind>
        {showUI && <MenuPanel webmap={webmap} view={view} sourcesForSearching = {sourcesForSearching} toggleSeaRiseLayers={(sliderValue, newSliderValue) => { toggleSeaRiseLayers(sliderValue, newSliderValue) }} extensionData={props.extensionData} />}
        <div className = {"container"}>
        {(props.isBadRoute || extensionActionFailed) && <CalciteAlert
         open
         autoClose = {true}
         autoCloseDuration = {'slow'}
         label = {"bad-route"}
         placement = {'bottom-end'}
         kind = {'danger'}
          >
          <span slot = {'message'}>{errorMessage}</span>
          </CalciteAlert>}
        {!showUI && <CalciteLoader className = {styles.loader} label = {"loader"}></CalciteLoader>}
        <div className={styles.mapDiv} ref={mapDiv}>
          </div>
        </div>
        <div id = "extraUItools" hidden = {!showUI}>
          <div id='togglerDiv' className={styles.newSitesDiv}>
          <div id='contactUsDiv' className={styles.newSitesLabel}><a href='https://www.epa.gov/cleanups/forms/contact-us' target='_blank'>Contact Us</a> | </div>
            <label className={styles.newSitesLabel}>Latest Sites in CIMC</label>
            <CalciteSwitch checked onCalciteSwitchChange={() => { toggleLatestSites() }} />
          <div id='lastUpdatedDiv'><LastUpdated webmap={webmap} /></div>
          <div className={styles.bmGallery} ref={bmGallery} />
          <div className={styles.scaleBar} ref={scaleDiv} />
          <div ref = {legendDiv}/>
        </div>
        </div>
      </CalciteShell>
    </>
  );

}
export default Map;